import * as React from "react";
import { BaseModal } from "../../../atoms/base-modal";
import { useState } from "react";
import apiClient from "../../../../application/apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import { LinkCopier } from "../../../molecules/copier";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import { reportUnexpectedError } from "../../../../error-reporter";
import { WarningBanner } from "@graffer-inc/console-design-kit";
import { PasswordResetConfirmModalContent } from "./password-reset-confirm-modal-content";
import { CloudGovApiError } from "../../../../application/domain/api-error";

interface ModalWithInputProps {
  isActive: boolean;
  user: UserEntity;
  onClose: () => void;
  title: string;
}

export const PasswordResetConfirmModal: React.FC<ModalWithInputProps> = ({
  isActive,
  user,
  onClose,
  title,
}) => {
  const [tmpPassword, setTmpPassword] = useState("");
  const [completeResetPassword, setCompleteResetPassword] = useState(false);
  const [doSendInvitation, setDoSendInvitation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const resetPassword = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await apiClient.resetPassword(user.id, doSendInvitation);
      setTmpPassword(data.tmpPassword);
      setCompleteResetPassword(true);
    } catch (e) {
      if (
        CloudGovApiError.isErrorCode(
          e,
          "org_user_with_same_email_already_exists",
        )
      ) {
        const err = new CloudGovApiError(e);
        const errResponse = err.response as any as {
          msg: string;
        };
        toast.error(
          `${user.email}は複数のアカウント(${errResponse.msg})に登録されているため、仮パスワード送信ができません。`,
        );
        return;
      }

      reportUnexpectedError(e);
      toast.error("パスワードリセットに失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, [user, setTmpPassword, setCompleteResetPassword, doSendInvitation]);

  const passwordResetCompletedContent = () => {
    return (
      <>
        <section className="modal-card-body">
          {completeResetPassword && doSendInvitation ? (
            <>
              <p>{user.email}に仮パスワードを送信しました。</p>
            </>
          ) : (
            <>
              <p>仮パスワードを以下に設定しました。</p>
              <p className="help">
                有効期限は1週間です。有効期限内に仮パスワードから変更されない場合は再度パスワードリセットが必要です。
              </p>
              <div className="mt-2">
                <LinkCopier text={tmpPassword} />
              </div>
            </>
          )}
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth is-failure"
            onClick={() => {
              setCompleteResetPassword(false);
              setDoSendInvitation(false);
              setTmpPassword("");
              onClose();
            }}
          >
            閉じる
          </button>
        </section>
      </>
    );
  };

  return (
    <BaseModal isActive={isActive} title={title} onClose={onClose}>
      {user.authMethod !== "password" ? (
        <>
          <section className="modal-card-body">
            <WarningBanner
              message="パスワードリセットは行えません"
              description="このユーザーはSAML認証のため、パスワードをリセットすることはできません。"
            />
          </section>
          <section className="modal-card-foot">
            <button
              type="button"
              className="button is-fullwidth is-failure"
              onClick={onClose}
            >
              閉じる
            </button>
          </section>
        </>
      ) : !completeResetPassword ? (
        <PasswordResetConfirmModalContent
          user={user}
          confirmMessage={
            <>
              <h2>以下のアカウントに対してパスワードリセットを行いますか?</h2>
              <div>
                なお、パスワードリセットを行うと二段階認証設定もリセットされます。
              </div>
            </>
          }
          submitLabel="リセットする"
          onSubmit={resetPassword}
          cancelLabel="キャンセルする"
          onCancel={onClose}
          isLoading={isLoading}
          doSendInvitation={doSendInvitation}
          setDoSendInvitation={setDoSendInvitation}
        />
      ) : (
        <>{passwordResetCompletedContent()}</>
      )}
    </BaseModal>
  );
};
